/**
 * ChartConfigs.ts
 * -------------------------------
 * Contains the configuration for all charts.
 * This includes the chart type, the component,
 * the axis options and the available chart settings.
 */

import LineChart from "@editor/items/charts/LineChart.vue";
import BarChart from "@editor/items/charts/BarChart.vue";
import SpiderChart from "@editor/items/charts/SpiderChart.vue";
import RadarChart from "@editor/items/charts/RadarChart.vue";

import type { Subset } from "@/helpers/utility/Type";
import type { IconName } from "@core/ui/lucideIcons";
import type {
  SelectableChartItemType,
  ChartType,
  ChartSettings,
  ChartLike
} from "./ChartItem";

export type PreSettings = Partial<ChartSettings> & {
  range?: [number, number];
};

export const charts: { [chartId: string]: ChartConfig } = {
  line: {
    id: "line",
    icon: "line-chart",
    component: LineChart,
    axisOptions: [
      {
        x: "simulationRound",
        y: "value",
        item: ["target", "resource", "index"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "range",

        hasSimulationColors: false,
        hasSimulationSymbols: true,
        hasRoundColors: false,
        hasItemColors: true,

        extraSettings: ["scaleColorsVisible"]
      },
      {
        x: "simulationRound",
        y: "impulseSum",
        item: ["target", "resource"],
        subgroups: ["increase", "decrease"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "range",

        hasSimulationColors: false,
        hasSimulationSymbols: true,
        hasRoundColors: false,
        hasItemColors: true,


        extraSettings: []
      }
    ],
    availableChartSettings: new Set([
      "xAxisLabelsShortened",
      "xAxisLabelsVisible",
      "xAxisTitleVisible",
      "yAxisLabelsVisible",
      "yAxisLabelsShortened",
      "yAxisTitleVisible",
      "groupLabelsVisible",
      "groupLabelsShortened",
      "backgroundFilled",
      "titleVisible",
      "fontSize",
      "roundEffectsVisible",
      "ticksVisible",
      "width",
      "height",
      "zoomed",
      "endPointsVisible"
    ])
  },
  bar: {
    id: "bar",
    icon: "bar-chart-3",
    component: BarChart,
    axisOptions: [
      {
        x: "target",
        y: "value",
        item: ["target"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,

        preSettings: {
          target: {
            range: [0, 100]
          }
        },
        extraSettings: ["areasColored", "zoomed"],

        defaults: {
          dataToggles: {
            all: {
              target: true
            }
          },
          chartSettings: {
            areasColored: false
          }
        }
      },
      {
        x: "target",
        y: "gain",
        item: ["target"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,

        preSettings: {
          all: {
            scaleColorsVisible: false,
            zoomed: true
          }
        },

        extraSettings: ["centered", "areasColored"],
      },
      {
        x: "target",
        y: "impulse",
        item: ["target"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          target: {
            zoomed: true,
            subValuesVisible: true,
            scaleColorsVisible: false
          }
        },
        extraSettings: ["centered"],
      },
      {
        x: "target",
        y: "effect",
        item: [
          "actionActivation",
          "incidentActivation",
          "scheduledEffectActivation"
        ],

        singleItem: false,
        singleSimulation: true,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: true,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          all: {
            zoomed: true,
            subValuesVisible: false,
            scaleColorsVisible: false
          }
        },

        extraSettings: ["filterZeroValues", "centered"],
      },
      {
        x: "resource",
        y: "value",
        item: ["resource"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            scaleColorsVisible: false
          }
        },
        extraSettings: ["zoomed"],
      },
      {
        x: "resource",
        y: "gain",
        item: ["resource"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            scaleColorsVisible: false
          }
        },
        extraSettings: ["centered"],
      },
      {
        x: "resource",
        y: "impulse",
        item: ["resource"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            subValuesVisible: true,
            scaleColorsVisible: false
          }
        },
        extraSettings: ["centered"],
      },
      {
        x: "resource",
        y: "effect",
        item: [
          "actionActivation",
          "incidentActivation",
          "scheduledEffectActivation"
        ],

        singleItem: false,
        singleSimulation: true,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: true,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            subValuesVisible: false,
            scaleColorsVisible: false
          }
        },

        extraSettings: ["filterZeroValues", "centered"],
      },

      {
        x: "index",
        y: "value",
        item: ["index"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            scaleColorsVisible: false
          }
        },
        extraSettings: ["zoomed"],
      },
      {
        x: "index",
        y: "gain",
        item: ["index"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "pair",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          resource: {
            scaleColorsVisible: false
          }
        },
        extraSettings: ["centered"],
      },

      {
        x: "simulationRound",
        y: "value",
        item: ["target", "resource", "index"],

        singleItem: true,
        singleSimulation: false,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,

        preSettings: {
          simulation: {
            range: [0, 100]
          },
          target: {
            range: [0, 100]
          },
          resource: {
            scaleColorsVisible: false
          }
        },
        extraSettings: ["roundEffectsVisible", "zoomed", "areasColored"],
      },
      {
        x: "simulationRound",
        y: "gain",
        item: ["target", "resource", "index"],

        singleItem: true,
        singleSimulation: false,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          all: {
            scaleColorsVisible: false,
            zoomed: true
          },
          simulation: {
            subValuesVisible: false
          }
        },
        extraSettings: ["roundEffectsVisible", "centered"],
      },
      {
        x: "simulationRound",
        y: "impulse",
        item: ["target", "resource"],

        singleItem: true,
        singleSimulation: false,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: false,

        preSettings: {
          all: {
            zoomed: true,
            subValuesVisible: true,
            scaleColorsVisible: false
          }
        },

        extraSettings: ["roundEffectsVisible", "centered"],
      }
    ],
    availableChartSettings: new Set([
      "fontSize",
      "xAxisLabelsVisible",
      "xAxisLabelsShortened",
      "yAxisLabelsVisible",
      "xAxisTitleVisible",
      "yAxisTitleVisible",
      "backgroundFilled",
      "ticksVisible",
      "titleVisible",
      "scaleColorsVisible",
      "width",
      "height",
      "endPointsVisible"
    ])
  },
  spider: {
    id: "spider",
    icon: "spider-chart",
    component: SpiderChart,
    axisOptions: [
      {
        x: "target",
        y: "value",
        item: ["target"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "list",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: true,
        hasItemColors: false,

        preSettings: {
          target: {
            range: [0, 100]
          }
        },

        defaults: {
          dataToggles: {
            all: {
              target: true
            }
          }
        },

        extraSettings: [],


      },
      {
        x: "target",
        y: "effect",
        item: [
          "actionActivation",
          "incidentActivation",
          "scheduledEffectActivation"
        ],

        singleItem: false,
        singleSimulation: true,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: true,
        hasRoundColors: false,
        hasItemColors: false,

        extraSettings: ["filterZeroValues"],


      },
      {
        x: "resource",
        y: "value",
        item: ["resource"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "list",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: true,
        hasItemColors: false,



        extraSettings: []
      },
      {
        x: "resource",
        y: "effect",
        item: [
          "actionActivation",
          "incidentActivation",
          "scheduledEffectActivation"
        ],

        singleItem: false,
        singleSimulation: true,
        roundMode: "range",

        hasSimulationSymbols: true,
        hasSimulationColors: true,
        hasRoundColors: false,
        hasItemColors: false,

        extraSettings: ["filterZeroValues"],


      },
      {
        x: "index",
        y: "value",
        item: ["index"],

        singleItem: false,
        singleSimulation: false,
        roundMode: "list",

        hasSimulationSymbols: true,
        hasSimulationColors: false,
        hasRoundColors: true,
        hasItemColors: false,



        extraSettings: []
      }
    ],
    availableChartSettings: new Set([
      "fontSize",
      "backgroundFilled",
      "xAxisLabelsVisible",
      "xAxisLabelsShortened",
      "yAxisLabelsVisible",
      "yAxisTitleVisible",
      "ticksVisible",
      "titleVisible",
      "width",
      "height",
      "endPointsVisible",
      "areasFilled"
    ])
  },
  radar: {
    id: "radar",
    icon: "radar-chart",
    component: RadarChart,
    axisOptions: [
      {
        x: "target",
        y: "value",
        item: ["target"],

        singleItem: false,
        singleSimulation: true,
        roundMode: "pair",

        hasSimulationSymbols: false,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,

        extraSettings: ["zoomed"],

        preSettings: {
          target: {
            range: [0, 100]
          }
        },

        defaults: {
          dataToggles: {
            all: {
              target: true
            }
          },
          chartSettings: {
            areasColored: true
          }
        },


      },
      {
        x: "resource",
        y: "value",
        item: ["resource"],

        singleItem: false,
        singleSimulation: true,
        roundMode: "pair",

        hasSimulationSymbols: false,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,



        defaults: {
          chartSettings: {
            areasColored: true
          }
        },

        extraSettings: []
      },
      {
        x: "index",
        y: "value",
        item: ["index"],

        singleItem: false,
        singleSimulation: true,
        roundMode: "pair",

        hasSimulationSymbols: false,
        hasSimulationColors: false,
        hasRoundColors: false,
        hasItemColors: true,



        defaults: {
          chartSettings: {
            areasColored: true
          }
        },

        extraSettings: []
      }
    ],
    availableChartSettings: new Set([
      "fontSize",
      "backgroundFilled",
      "xAxisLabelsVisible",
      "xAxisLabelsShortened",
      "yAxisLabelsVisible",
      "yAxisTitleVisible",
      "areasColored",
      "ticksVisible",
      "titleVisible",
      "width",
      "height"
    ])
  }
};

export interface ChartConfig {
  id: ChartType;
  icon: IconName;
  component: ChartComponentType;
  axisOptions: ChartAxisOption[];
  availableChartSettings: Set<keyof ChartSettings>;
}

export type ChartAxisOption = {
  item: SelectableChartItemType[];
  singleItem: boolean;
  singleSimulation: boolean;
  hasSimulationSymbols: boolean;
  hasSimulationColors: boolean;
  hasRoundColors: boolean;
  hasItemColors?: boolean;
  x: ChartSettings["xAxis"];
  y: ChartSettings["yAxis"];
  roundMode: "single" | "pair" | "list" | "range";
  preSettings?: {
    [type: string]: PreSettings;
  };
  extraSettings?: (keyof ChartSettings)[];
  subgroups?: string[];

  defaults?: Subset<ChartLike>;
};

export type ChartComponentType =
  | typeof LineChart
  | typeof BarChart
  | typeof SpiderChart
  | typeof RadarChart;
